/**
 * 权限关联表：勾选 key 时建议同时勾选 value 里列出的依赖。
 *
 * key 格式: `${module}.${action}`
 * value: [{ module, action, reason }]
 *
 * 每条 reason 说明依赖的来源（哪个页面哪个接口），方便审查与维护。
 * 发现新依赖时，往此表加一行即可。
 */
export const PERMISSION_DEPENDENCIES = {
  'app_pages.analytics': [
    { module: 'courses', action: 'view', reason: '数据页加载需要拉取课程列表（GET /courses）' }
  ],
  'learning_report.edit_options': [
    { module: 'learning_report', action: 'view', reason: '编辑候选项需要先进入课堂记录页面' }
  ]
}

/**
 * 判断 permissionsMap 中是否已经具备 (module, action)
 * permissionsMap 形如 { courses: ['view', 'edit'], app_pages: ['*'] }
 */
function hasAction(permissionsMap, module, action) {
  const acts = permissionsMap?.[module]
  if (!Array.isArray(acts)) return false
  return acts.includes('*') || acts.includes(action)
}

/**
 * 给定一项已勾权限，返回它还缺哪些依赖（未在 permissionsMap 中）。
 */
export function findMissingDepsFor(permissionsMap, module, action) {
  const key = `${module}.${action}`
  const deps = PERMISSION_DEPENDENCIES[key] || []
  return deps.filter((dep) => !hasAction(permissionsMap, dep.module, dep.action))
}

/**
 * 扫描整张权限组：遍历所有已勾权限，汇总尚未满足的依赖（按 module+action 去重）。
 */
export function findAllMissingDeps(permissionsMap) {
  const seen = new Set()
  const missing = []
  Object.entries(permissionsMap || {}).forEach(([module, actions]) => {
    if (!Array.isArray(actions)) return
    actions.forEach((action) => {
      if (action === '*') return
      const deps = findMissingDepsFor(permissionsMap, module, action)
      deps.forEach((dep) => {
        const k = `${dep.module}.${dep.action}`
        if (seen.has(k)) return
        seen.add(k)
        missing.push({ ...dep, triggeredBy: { module, action } })
      })
    })
  })
  return missing
}

/**
 * 把 (module, action) 翻译成人类可读名称，便于在提示里展示。
 * 依赖 permissionFields（来自后端 getPermissionFields）。
 */
export function describePermission(permissionFields, module, action) {
  const moduleConfig = permissionFields?.[module]
  if (!moduleConfig) return `${module}.${action}`
  const moduleName = moduleConfig.name || module
  for (const optionGroup of Object.values(moduleConfig.options || {})) {
    const found = (optionGroup.values || []).find((v) => v.value === action)
    if (found) return `${moduleName} → ${found.name}`
  }
  return `${moduleName} → ${action}`
}
